/**
 * Palisade win-probability estimator ("advantage meter").
 *
 *   winProbability(state) -> { p0, p1 }   (p0 + p1 = 1, both in 0..1)
 *
 * Pure browser-compatible JS: depends only on the public core contract
 * (src/core/index.js). Deterministic, no rng, no learning at runtime;
 * one call costs two character/gimmick-aware BFS runs (well under 1ms
 * on a 9x9 board).
 *
 * Model: hand-picked features in the PLAYER-TO-MOVE's perspective,
 * linear combination, logistic squash. Framing everything from the
 * mover's side makes the estimate exactly symmetric: mirroring the
 * board (swap players, flip rows) yields the same feature vector for
 * the new mover, so p0/p1 swap.
 *
 * Features (see extractFeatures):
 *   bias      — tempo: having the move is worth a little by itself
 *   dd        — shortestPathLength(opponent) - shortestPathLength(mover).
 *               Core's shortestPathLength already folds in ninja jump
 *               budgets, ice slides and swamp semantics, so the same
 *               feature works for character/gimmick games.
 *   wd        — wallsLeft(mover) - wallsLeft(opponent)
 *   swampDiff — swamped(opponent) - swamped(mover): a swamped player
 *               loses a movement turn that dd cannot see
 *   abilityDiff — unspent active-ability charges, mover - opponent
 *   raceWin   — 1 if the opponent has no walls left and the mover's path
 *               is not longer (a sprint wins the race; hard AI plays it)
 *   raceLose  — 1 if the mover has no walls left and the opponent's path
 *               is strictly shorter (the mirrored losing race)
 *
 * The weights below were fitted offline by logistic regression on
 * self-play data (sim/calibrate-winprob.mjs: normal-vs-normal and
 * hard-vs-hard games, plus character/gimmick games, fixed seeds) and
 * burned in as constants. See that script's report for the calibration
 * table and Brier score.
 */

import { shortestPathLength } from '../core/index.js';

/** Feature names, index-aligned with extractFeatures() and WEIGHTS. */
export const FEATURE_NAMES = [
  'bias',
  'dd',
  'wd',
  'swampDiff',
  'abilityDiff',
  'raceWin',
  'raceLose',
];

/**
 * Logistic-regression coefficients, fitted by sim/calibrate-winprob.mjs
 * with seed 42: 340 self-play games (220 normal-vs-normal + 60
 * hard-vs-hard classic, 60 character/gimmick), 22,991 positions, 80/20
 * game-level holdout (18,401 train / 4,590 holdout). Holdout Brier
 * score 0.1758 (0.25 = uninformative). Do not tweak by hand: re-run
 * the calibration script and paste its output here.
 */
export const WEIGHTS = [
  0.123297, // bias
  0.339141, // dd
  0.518639, // wd
  1.219737, // swampDiff
  1.266904, // abilityDiff
  3.601616, // raceWin
  -3.43772, // raceLose
];

function abilityUses(player) {
  return player.ability && player.ability.usesLeft > 0
    ? player.ability.usesLeft
    : 0;
}

function isSwamped(player) {
  return player.effects && player.effects.swamped ? 1 : 0;
}

/**
 * Feature vector from the PLAYER-TO-MOVE's perspective, index-aligned
 * with FEATURE_NAMES / WEIGHTS. Exported so the offline calibration
 * script trains on exactly the vector used at inference time.
 *
 * Only meaningful for live positions (state.winner === null);
 * winProbability short-circuits decided games before calling this.
 */
export function extractFeatures(state) {
  const me = state.current;
  const opp = 1 - me;
  const dMe = shortestPathLength(state, me);
  const dOpp = shortestPathLength(state, opp);
  const wMe = state.players[me].wallsLeft;
  const wOpp = state.players[opp].wallsLeft;
  return [
    1, // bias
    dOpp - dMe, // dd
    wMe - wOpp, // wd
    isSwamped(state.players[opp]) - isSwamped(state.players[me]), // swampDiff
    abilityUses(state.players[me]) - abilityUses(state.players[opp]),
    wOpp === 0 && dMe <= dOpp ? 1 : 0, // raceWin
    wMe === 0 && dOpp < dMe ? 1 : 0, // raceLose
  ];
}

function sigmoid(z) {
  return 1 / (1 + Math.exp(-z));
}

/**
 * Estimate the winning chances of both players in `state`.
 * Returns { p0, p1 } with p0 + p1 === 1, each in [0, 1].
 * Decided games return exactly 1/0. Works on any JSON-serializable
 * core state, including character abilities and ice/swamp gimmicks.
 */
export function winProbability(state) {
  if (state.winner === 0) return { p0: 1, p1: 0 };
  if (state.winner === 1) return { p0: 0, p1: 1 };

  const me = state.current;
  const dMe = shortestPathLength(state, me);
  const dOpp = shortestPathLength(state, 1 - me);

  let pMover;
  if (dMe === 0) {
    // On the goal row without winner set: cannot happen via legal play,
    // but a hand-built state deserves a sane answer.
    pMover = 1;
  } else if (dMe < 0 && dOpp < 0) {
    pMover = 0.5; // both sealed in (illegal by the rules): pure guess
  } else if (dMe < 0) {
    pMover = 0; // mover has no path at all
  } else if (dOpp < 0) {
    pMover = 1; // opponent has no path at all
  } else {
    const x = extractFeatures(state);
    let z = 0;
    for (let i = 0; i < WEIGHTS.length; i++) z += WEIGHTS[i] * x[i];
    pMover = sigmoid(z);
  }

  return me === 0 ? { p0: pMover, p1: 1 - pMover } : { p0: 1 - pMover, p1: pMover };
}
